import React, { useState, useEffect, MouseEventHandler } from 'react';

import { childrenComponents, PaginationChildren } from './pagination';
import { LucidTheme } from '../../types/types';
import Box from '../box/box';
import Link from '../link/link';
import Text from '../text/text';

export interface ViewMorePaginationProps<T> {
  /** The children that are returned with the `Pagination` component. */
  children: PaginationChildren<T>;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Starts the array of items at a different page. */
  startPage?: number;
  /** The list of items to return with pagination. */
  items: T[];
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** The text to be shown when there are no results. */
  noItemsText?: string;
  /** The onClick handler callback. */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  /** Checks whether or not the items should always be visible. */
  shouldAlwaysBeVisible?: boolean;
  /** Checks whether the noItemsText should be displayed. */
  shouldShowNoItemsText?: boolean;
  /** The root url alias for SEO crawling. */
  urlAlias?: string;
  /** The text to be shown in the pagination link. */
  viewMoreText?: string;
}

const ViewMorePagination = <T,>({
  shouldAlwaysBeVisible = false,
  children,
  className,
  startPage = 1,
  noItemsText = 'There are currently no results.',
  onClick,
  items,
  itemsPerPage = 5,
  shouldShowNoItemsText = false,
  urlAlias,
  viewMoreText = 'View more',
}: ViewMorePaginationProps<T>) => {
  const paginationItemStart = (startPage - 1) * itemsPerPage;
  const [activePage, setActivePage] = useState(startPage);
  const visibleItemStart = (activePage - 1) * itemsPerPage;
  const visibleItemEnd = visibleItemStart + itemsPerPage;
  const visibleItems = items.slice(visibleItemStart, visibleItemEnd);
  const [currentItems, setCurrentItems] = useState<T[]>(visibleItems);
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const hasMore = activePage < totalPages;
  const hasItems = !!items.length;
  const paginationURL = urlAlias ? `${urlAlias}/${activePage + 1}` : undefined;

  useEffect(() => {
    if (shouldAlwaysBeVisible) {
      const previousItems = items.slice(paginationItemStart, visibleItemStart);
      if (!!previousItems.length && visibleItemStart !== 0) {
        return setCurrentItems([...previousItems, ...visibleItems]);
      }
    }

    return setCurrentItems(visibleItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, items, itemsPerPage, paginationItemStart, shouldAlwaysBeVisible, startPage, visibleItemStart]);

  const handleOnClick: MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault();
    setActivePage(activePage + 1);

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Box
      css={{
        label: 'viewMorePagination',
        width: '100%',
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      {hasItems &&
        childrenComponents(children, {
          activePage,
          itemsPerPage,
          totalPages,
          visibleItems: currentItems,
        })}
      {!hasItems && shouldShowNoItemsText && (
        <Text
          css={(theme: LucidTheme) => ({
            textAlign: 'center',
            flex: '0 1 100%',
            marginBottom: theme.space.s48,
          })}
        >
          {noItemsText}
        </Text>
      )}
      {hasMore && (
        <Box
          as="section"
          css={(theme: LucidTheme) => ({
            label: 'hasMoreLink',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            position: 'relative',
            padding: `${theme.space[6]}px 0`,
            alignItems: 'center',
            width: '100%',
          })}
        >
          <hr
            css={(theme: LucidTheme) => ({
              border: 0,
              borderBottom: `2px solid ${theme.colors.coolGrey[10]}`,
              margin: 0,
              width: '100%',
            })}
          />
          <Link
            hasArrow
            css={(theme: LucidTheme) => ({
              cursor: 'pointer',
              padding: theme.space[2],
              whiteSpace: 'nowrap',
              background: 'transparent',
              border: 0,
              fontSize: theme.fontSizes.fs16,
              color: theme.colors.secondary,
              '&:visited': {
                color: theme.colors.secondary,
              },
              '&:hover': {
                color: theme.colors.secondary,
              },
            })}
            onClick={handleOnClick}
            rel="next"
            role="button"
            to={paginationURL}
          >
            {viewMoreText}
          </Link>
        </Box>
      )}
    </Box>
  );
};

export { ViewMorePagination };
export default ViewMorePagination;
